import { TextField } from '@consta/uikit/TextField'
//@ts-ignore
import classes from './AccountContent.module.scss'
import { Text } from '@consta/uikit/Text'
import { NavLink, useNavigate } from 'react-router-dom'
import { Checkbox } from '@consta/uikit/Checkbox'
import { Button } from '@consta/uikit/Button'
import { useState, useEffect, useCallback } from 'react'
import { Card } from '@consta/uikit/Card'
import { Avatar } from '@consta/uikit/Avatar';
import { Modal } from '@consta/uikit/Modal';
import { IconUpload } from '@consta/icons/IconUpload';
import { IconClose } from '@consta/icons/IconClose';
import PrivacyPolicy from '../../Modals/PrivacyPolicy/PrivacyPolicy'
import { useSelector, useDispatch } from 'react-redux'
import { RootState } from '../../../store/store'
import { useRegistrationMutation, useChangePasswordMutation, useChangePasswordCodeMutation } from '../../../services/authService'
import { setUserInfo } from '../../../store/authSlice'
import { LS_KEYS } from '../../../store/constants'
import { useLazyUserInfoQuery, usePatchUserMutation } from '../../../services/userService'
import { useLazyGetUserCommentQuery } from '../../../services/commentService'
import { useLazyGetProjectQuery } from '../../../services/projectService'
// import { IconMessage } from '@consta/icons/IconMessage'

// Типы для формы регистрации
interface RegistrationFormData {
    last_name: string;
    first_name: string;
    middle_name: string;
    phone: string;
    position: string;
}

// Типы для формы смены пароля
interface ChangePasswordFormData {
    old_password: string;
    new_password: string;
    confirm_password: string;
}

const AccountContent = () => {
    const navigate = useNavigate()
    const dispatch = useDispatch()
    const [checked, setChecked] = useState<boolean>(false)
    const [privacyPolicy, setPrivacyPolicy] = useState(false)
    const [isRegistered, setIsRegistered] = useState<boolean | null>(null)
    const [userAccessLevel, setUserAccessLevel] = useState<number | null>(null)
    const [isChangePasswordModalOpen, setIsChangePasswordModalOpen] = useState(false)
    const [isAvatarModalOpen, setIsAvatarModalOpen] = useState(false)
    const [selectedFile, setSelectedFile] = useState<File | null>(null)
    const [previewUrl, setPreviewUrl] = useState<string | null>(null)
    const [isDragOver, setIsDragOver] = useState(false)
    const [projects, setProjects] = useState<any[]>([])
    const [isProjectsLoading, setIsProjectsLoading] = useState(false)
    const [isCodeSent, setIsCodeSent] = useState(false);
    const [code, setCode] = useState('');
    const [changePasswordError, setChangePasswordError] = useState<string>('');
    const [remainingPasswordAttempts, setRemainingPasswordAttempts] = useState<number | null>(null);
    const [isPasswordBlocked, setIsPasswordBlocked] = useState(false);
    const [registrationError, setRegistrationError] = useState<string>('');

    const [changePasswordCode, { isLoading: isCodeSending }] = useChangePasswordCodeMutation();

    // Состояния для полей формы регистрации
    const [formData, setFormData] = useState<RegistrationFormData>({
        last_name: '',
        first_name: '',
        middle_name: '',
        phone: '',
        position: ''
    })

    // Состояния для полей формы смены пароля
    const [passwordFormData, setPasswordFormData] = useState<ChangePasswordFormData>({
        old_password: '',
        new_password: '',
        confirm_password: ''
    })

    const [errors, setErrors] = useState<Partial<RegistrationFormData>>({})
    const [passwordErrors, setPasswordErrors] = useState<Partial<ChangePasswordFormData>>({})

    // Получаем userInfo из Redux store
    const userInfo = useSelector((state: RootState) => state.auth.userInfo);

    // Хук для регистрации
    const [registration, { isLoading: isRegistrationLoading }] = useRegistrationMutation();

    // Хук для смены пароля
    const [changePassword, { isLoading: isChangePasswordLoading }] = useChangePasswordMutation();

    // Хук для обновления пользователя (загрузка аватара)
    const [patchUser, { isLoading: isAvatarLoading }] = usePatchUserMutation();

    // Lazy query для получения userInfo
    const [fetchUserInfo, { isLoading: isUserInfoLoading }] = useLazyUserInfoQuery();

    // Lazy query для получения комментариев
    const [fetchComments, { data: commentsData, isLoading: isCommentsLoading }] = useLazyGetUserCommentQuery();

    const [getProjects] = useLazyGetProjectQuery();

    useEffect(() => {
        if (userInfo) {
            setIsRegistered(userInfo.is_registered || false);
            setUserAccessLevel(userInfo.role?.access_level || 0);
        } else {
            const storedUserInfo = localStorage.getItem('userInfo');
            if (storedUserInfo) {
                try {
                    const parsedUserInfo = JSON.parse(storedUserInfo);
                    setIsRegistered(parsedUserInfo.is_registered || false);
                    setUserAccessLevel(parsedUserInfo.role?.access_level || 0);
                } catch (error) {
                    console.error('Ошибка парсинга userInfo из localStorage:', error);
                    setIsRegistered(false);
                    setUserAccessLevel(0);
                }
            } else {
                setIsRegistered(false);
                setUserAccessLevel(0);
            }
        }
    }, [userInfo]);

    useEffect(() => {
        if (isRegistered) {
            fetchComments({ limit: 5 });
        }
    }, [isRegistered, fetchComments]);

    useEffect(() => {
        if (isRegistered && shouldShowProjects() && userInfo?.company?.uuid) {
            loadProjects();
        }
    }, [isRegistered, userInfo]);

    const loadProjects = async () => {
        if (!userInfo?.company?.uuid) return;

        setIsProjectsLoading(true);
        try {
            const result = await getProjects({
                limit: 100,
                offset: 0,
                companies: userInfo.company?.uuid
            }).unwrap();

            const userCompanyProjects = result.results.filter(project =>
                project.company === userInfo.company.uuid
            );

            setProjects(userCompanyProjects);
        } catch (error) {
            console.error('Ошибка загрузки проектов:', error);
            setProjects([]);
        } finally {
            setIsProjectsLoading(false);
        }
    };

    // Обработчики для drag&drop
    const handleDragOver = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        setIsDragOver(true);
    }, []);

    const handleDragLeave = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        setIsDragOver(false);
    }, []);

    const handleDrop = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        setIsDragOver(false);

        const files = e.dataTransfer.files;
        if (files && files[0]) {
            handleFileSelect(files[0]);
        }
    }, []);

    const handleFileSelect = (file: File) => {
        if (!file.type.startsWith('image/')) {
            alert('Пожалуйста, выберите файл изображения');
            return;
        }

        if (file.size > 5 * 1024 * 1024) {
            alert('Размер файла не должен превышать 5MB');
            return;
        }

        setSelectedFile(file);

        const reader = new FileReader();
        reader.onload = (e) => {
            setPreviewUrl(e.target?.result as string);
        };
        reader.readAsDataURL(file);
    };

    const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            handleFileSelect(file);
        }
    };

    // Функция для загрузки аватара
    const handleAvatarUpload = async () => {
        if (!selectedFile || !userInfo?.uuid) {
            alert('Пожалуйста, выберите файл');
            return;
        }

        try {
            const formData = new FormData();
            formData.append('profile_image', selectedFile);

            const result = await patchUser({
                uuid: userInfo.uuid,
                formData: formData
            }).unwrap();


            const updatedUserInfo = await fetchUserInfo().unwrap();
            dispatch(setUserInfo(updatedUserInfo));

            handleCloseAvatarModal();
            alert('Аватар успешно обновлен!');

        } catch (error) {
            console.error('Ошибка загрузки аватара:', error);
            alert('Ошибка загрузки аватара. Пожалуйста, попробуйте снова.');
        }
    };

    // Закрытие модального окна загрузки аватара
    const handleCloseAvatarModal = () => {
        setIsAvatarModalOpen(false);
        setSelectedFile(null);
        setPreviewUrl(null);
        setIsDragOver(false);
    };

    // Обработчики изменения полей форм
    const handleInputChange = (field: keyof RegistrationFormData) => (value: string | null) => {
        setFormData(prev => ({
            ...prev,
            [field]: value || ''
        }))
        if (errors[field]) {
            setErrors(prev => ({
                ...prev,
                [field]: undefined
            }))
        }
    }

    const handlePasswordInputChange = (field: keyof ChangePasswordFormData) => (value: string | null) => {
        setPasswordFormData(prev => ({
            ...prev,
            [field]: value || ''
        }))
        if (passwordErrors[field]) {
            setPasswordErrors(prev => ({
                ...prev,
                [field]: undefined
            }))
        }
    }

    // Валидация формы регистрации
    const validateForm = (): boolean => {
        const newErrors: Partial<RegistrationFormData> = {}

        if (!formData.last_name.trim()) newErrors.last_name = 'Фамилия обязательна'
        if (!formData.first_name.trim()) newErrors.first_name = 'Имя обязательно'
        if (!formData.phone.trim()) newErrors.phone = 'Номер телефона обязателен'
        if (!formData.position.trim()) newErrors.position = 'Должность обязательна'

        setErrors(newErrors)
        return Object.keys(newErrors).length === 0
    }

    // Валидация формы смены пароля
    const validatePasswordForm = (): boolean => {
        const newErrors: Partial<ChangePasswordFormData> = {};

        if (isCodeSent) {
            if (!code.trim()) {
                // Для кода ошибка будет обрабатываться отдельно
            }
            if (!passwordFormData.new_password.trim()) {
                newErrors.new_password = 'Новый пароль обязателен';
            }
            if (!passwordFormData.confirm_password.trim()) {
                newErrors.confirm_password = 'Подтверждение пароля обязательно';
            }
            if (passwordFormData.new_password !== passwordFormData.confirm_password) {
                newErrors.confirm_password = 'Пароли не совпадают';
            }
            if (passwordFormData.new_password.length < 6) {
                newErrors.new_password = 'Пароль должен содержать минимум 6 символов';
            }
        }

        setPasswordErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Функция для получения и обновления информации о пользователе
    const updateUserInfo = async () => {
        try {
            const userInfoResult = await fetchUserInfo().unwrap();
            console.log('UserInfo получен после регистрации:', userInfoResult);

            dispatch(setUserInfo(userInfoResult));
            localStorage.setItem(LS_KEYS.userInfo, JSON.stringify(userInfoResult));

            return userInfoResult;
        } catch (err) {
            console.error('Ошибка при получении userInfo:', err);
            throw err;
        }
    }

    // Обработчик регистрации
    const handleRegistration = async () => {
        if (!validateForm() || !checked) {
            return
        }

        setRegistrationError('');

        try {
            const registrationResult = await registration(formData).unwrap();
            console.log('Регистрация успешна:', registrationResult);

            const userInfoData = await updateUserInfo();

            setIsRegistered(true);
            setUserAccessLevel(userInfoData.role?.access_level || 0);

            navigate('/account');

        } catch (err: any) {
            console.error('Ошибка регистрации:', err);
            if (err.data) {
                if (typeof err.data === 'string') {
                    setRegistrationError(err.data);
                } else if (err.data.detail) {
                    setRegistrationError(err.data.detail);
                } else if (err.data.non_field_errors) {
                    setRegistrationError(err.data.non_field_errors.join(', '));
                } else {
                    setRegistrationError('Ошибка регистрации. Пожалуйста, проверьте введенные данные.');
                }
            } else {
                setRegistrationError('Ошибка регистрации. Пожалуйста, попробуйте снова.');
            }
        }
    }

    // Обработчик отправки кода для смены пароля
    const handleSendCode = async () => {
        if (!passwordFormData.old_password.trim()) {
            setPasswordErrors({ old_password: 'Старый пароль обязателен' });
            return;
        }

        setChangePasswordError('');
        setRemainingPasswordAttempts(null);
        setIsPasswordBlocked(false);

        try {
            await changePasswordCode({
                old_password: passwordFormData.old_password
            }).unwrap();

            setIsCodeSent(true);
            console.log('Код отправлен успешно');

        } catch (error: any) {
            console.error('Ошибка отправки кода:', error);
            
            if (error.data) {
                if (error.data.error) {
                    const errorMessage = error.data.error;
                    setChangePasswordError(errorMessage);
                    
                    if (errorMessage.includes('Ваш аккаунт временно заблокирован')) {
                        setIsPasswordBlocked(true);
                    }
                } else if (error.data.detail) {
                    setChangePasswordError(error.data.detail);
                } else {
                    setChangePasswordError('Ошибка отправки кода. Проверьте старый пароль и попробуйте снова.');
                }
            } else {
                setChangePasswordError('Ошибка отправки кода. Проверьте старый пароль и попробуйте снова.');
            }
        }
    };

    // Обработчик смены пароля
    const handleChangePassword = async () => {
        if (!validatePasswordForm()) {
            return;
        }

        setChangePasswordError('');
        setRemainingPasswordAttempts(null);

        try {
            const changePasswordResult = await changePassword({
                code: code,
                new_password: passwordFormData.new_password,
                confirm_password: passwordFormData.confirm_password
            }).unwrap();

            console.log('Пароль успешно изменен:', changePasswordResult);

            handleClosePasswordModal();

        } catch (error: any) {
            console.error('Ошибка смены пароля:', error);
            
            if (error.data) {
                if (error.data.error) {
                    const errorMessage = error.data.error;
                    setChangePasswordError(errorMessage);
                    
                    if (errorMessage.includes('Неверный код верификации')) {
                        const attemptsMatch = errorMessage.match(/Осталось попыток: (\d+)/);
                        if (attemptsMatch) {
                            const attempts = parseInt(attemptsMatch[1]);
                            setRemainingPasswordAttempts(attempts);
                        }
                    }
                    
                    if (errorMessage.includes('Превышено количество попыток ввода кода')) {
                        setIsPasswordBlocked(true);
                        handleLogout();
                    }
                } else if (error.data.detail) {
                    setChangePasswordError(error.data.detail);
                } else {
                    setChangePasswordError('Ошибка смены пароля. Проверьте код и попробуйте снова.');
                }
            } else {
                setChangePasswordError('Ошибка смены пароля. Проверьте код и попробуйте снова.');
            }
        }
    };

    // Функция логаута
    const handleLogout = () => {
        localStorage.removeItem(LS_KEYS.token);
        localStorage.removeItem(LS_KEYS.refreshToken);
        localStorage.removeItem(LS_KEYS.userInfo);
        localStorage.removeItem('loginEmail');
        //@ts-ignore
        dispatch(setUserInfo(null));
        navigate('/login');
    };

    // Закрытие модального окна смены пароля
    const handleClosePasswordModal = () => {
        setIsChangePasswordModalOpen(false);
        setIsCodeSent(false);
        setCode('');
        setPasswordFormData({
            old_password: '',
            new_password: '',
            confirm_password: ''
        });
        setPasswordErrors({});
        setChangePasswordError('');
        setRemainingPasswordAttempts(null);
        setIsPasswordBlocked(false);
    };

    // Форматирование даты
    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('ru-RU');
    };

    // Получение имени пользователя из комментария
    const getUserDisplayName = (comment: any) => {
        if (comment.user_fio) {
            return comment.user_fio;
        }
    };

    // Если статус еще не загружен, показываем заглушку
    if (isRegistered === null || userAccessLevel === null) {
        return (
            <div className={classes.main}>
                <div className={classes.container}>
                    <Text>Загрузка...</Text>
                </div>
            </div>
        );
    }

    // Функция для проверки, нужно ли показывать проекты
    const shouldShowProjects = () => {
        return userAccessLevel === 0;
    };

    // Форматирование даты для проектов
    const formatProjectDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('ru-RU');
    };

    const isLoading = isRegistrationLoading || isUserInfoLoading;

    return (
        <div>
            {!isRegistered ? (
                // Форма регистрации для незарегистрированных пользователей
                <div className={classes.main}>
                    <div className={classes.container}>
                        <div className={classes.text}>
                            <Text size='2xl' weight='bold'>Личный кабинет</Text>
                            <Text size='s' view='secondary'>Заполните форму для регистрации</Text>
                        </div>
                        <div className={classes.name}>
                            <TextField
                                size='s'
                                label='Фамилия'
                                placeholder='Иванов'
                                value={formData.last_name}
                                onChange={handleInputChange('last_name')}
                                status={errors.last_name ? 'alert' : undefined}
                                caption={errors.last_name}
                            />
                            <TextField
                                size='s'
                                label='Имя'
                                placeholder='Иван'
                                value={formData.first_name}
                                onChange={handleInputChange('first_name')}
                                status={errors.first_name ? 'alert' : undefined}
                                caption={errors.first_name}
                            />
                            <TextField
                                size='s'
                                label='Отчество'
                                placeholder='Иванович'
                                value={formData.middle_name}
                                onChange={handleInputChange('middle_name')}
                            />
                        </div>
                        <TextField
                            size='s'
                            label='Номер телефона'
                            placeholder='+7 (___) ___-__-__'
                            value={formData.phone}
                            onChange={handleInputChange('phone')}
                            status={errors.phone ? 'alert' : undefined}
                            caption={errors.phone}
                        />
                        <TextField
                            size='s'
                            label='Должность'
                            placeholder='Например: инженер-геолог'
                            value={formData.position}
                            onChange={handleInputChange('position')}
                            status={errors.position ? 'alert' : undefined}
                            caption={errors.position}
                        />

                        {/* ЧЕКБОКС */}
                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', marginBottom: '16px' }}>
                            <Checkbox
                                size='s'
                                checked={checked}
                                onChange={() => setChecked(!checked)}
                            />
                            <Text size='s' style={{ marginTop: '2px' }}>
                                Я соглашаюсь с{' '}
                                <span
                                    style={{ color: '#0078d2', cursor: 'pointer' }}
                                    onClick={() => setPrivacyPolicy(true)}
                                >
                                    политикой конфиденциальности
                                </span>{' '}
                                и даю согласие на обработку персональных данных
                            </Text>
                        </div>

                        {/* Отображение ошибки API */}
                        {registrationError && (
                            <Text size='s' view='alert' style={{ marginBottom: '16px' }}>
                                {registrationError}
                            </Text>
                        )}

                        <Button
                            label={isLoading ? 'Регистрация...' : 'Зарегистрироваться'}
                            onClick={handleRegistration}
                            disabled={!checked || isLoading}
                            loading={isLoading}
                        />
                    </div>
                    <Text size='xs' view='secondary'>© {new Date().getFullYear()} ООО «Геоиндикатор»</Text>
                </div>
            ) : (
                // Личный кабинет для зарегистрированных пользователей
                <div className={classes.main}>
                    <Card verticalSpace="l" horizontalSpace="l" className={classes.cardContainerOptions}>
                        <div className={classes.cardContainer}>
                            <Avatar
                                url={userInfo?.profile_image || '/platform/pics/avatar.jpg'}
                                name={`${userInfo?.last_name || ''} ${userInfo?.first_name || ''} ${userInfo?.middle_name || ''}`}
                                style={{ width: '150px', height: '150px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}
                            />
                            <div className={classes.info}>
                                <Text size='2xl' weight='bold'>
                                    {`${userInfo?.last_name || ''} ${userInfo?.first_name || ''} ${userInfo?.middle_name || ''}`.trim()}
                                </Text>
                                <Text view='secondary'>
                                    {userInfo?.position || 'Должность не указана'} | {userInfo?.company?.name || 'Компания не указана'}
                                </Text>
                                <div className={classes.under}>
                                    <div className={classes.email}>
                                        <Text size='s'>Электронная почта: {userInfo?.email || 'Не указана'}</Text>
                                        <Text size='s'>Дата регистрации: {
                                            //@ts-ignore
                                            userInfo?.created_at
                                                //@ts-ignore
                                                ? new Date(userInfo.created_at).toLocaleDateString('ru-RU')
                                                : 'Не указана'}</Text>
                                        <Button
                                            size='xs'
                                            label='Загрузить аватар'
                                            onClick={() => setIsAvatarModalOpen(true)}
                                        />
                                    </div>
                                    <div className={classes.number}>
                                        <Text size='s'>Телефон: {userInfo?.phone || 'Не указан'}</Text>
                                        <Text size='s'>Должность: {userInfo?.position || 'Не указана'}</Text>
                                        <Button
                                            size='xs'
                                            label='Изменить пароль'
                                            onClick={() => setIsChangePasswordModalOpen(true)}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </Card>

                    {/* Проекты - показываем только для access_level = 0 */}
                    {shouldShowProjects() && (
                        <div className={classes.project}>
                            <Text weight='bold' size='xl'>Проекты организации</Text>

                            {isProjectsLoading ? (
                                <Text>Загрузка проектов...</Text>
                            ) : projects.length > 0 ? (
                                <div className={classes.projectCards}>
                                    {projects.map((project) => (
                                        <Card key={project.uuid} verticalSpace="l" horizontalSpace="l" className={classes.cardContainerOptions}>
                                            <NavLink to={`/project/${project.uuid}`} style={{ color: '#0078d2', textDecoration: 'none' }}>
                                                <Text weight='bold'>
                                                    {project.name} {project.number ? `- ${project.number}` : ''}
                                                </Text>
                                            </NavLink>
                                            <Text size='s'>{project.description}</Text>
                                            <Text size='s'>Статус: {project.is_completed ? 'Завершен' : 'В работе'}</Text>
                                            <Text size='xs' view='secondary'>
                                                Сроки: {formatProjectDate(project.start_date)} - {formatProjectDate(project.end_date)}
                                            </Text>
                                        </Card>
                                    ))}
                                </div>
                            ) : (
                                <Text size='s' view='secondary'>Проекты отсутствуют</Text>
                            )}
                        </div>
                    )}
                    <div className={classes.recentComments}>
                        <div className={classes.commentsSection}>
                            <div className={classes.sectionHeader}>
                                {/* <IconMessage size="m" /> */}
                                <Text weight='bold' size='xl'>Последние комментарии</Text>
                            </div>

                            {isCommentsLoading ? (
                                <div className={classes.loadingState}>
                                    <Text>Загрузка комментариев...</Text>
                                </div>
                            ) : commentsData && Array.isArray(commentsData) && commentsData.length > 0 ? (
                                <div className={classes.commentCards}>
                                    {commentsData.slice(0, 5).map((comment: any) => (
                                        <Card key={comment.id} verticalSpace="m" horizontalSpace="m" className={classes.commentCard}>
                                            <div className={classes.commentHeader}>
                                                <div className={classes.commentAuthor}>
                                                    <Text size='s' weight='semibold'>
                                                        {getUserDisplayName(comment)}
                                                    </Text>
                                                    <Text size='xs' view='secondary'>
                                                        {formatDate(comment.created_at)}
                                                    </Text>
                                                </div>
                                                {comment.object_name && (
                                                    <div className={classes.commentObject}>
                                                        <Text size='xs' view='brand'>
                                                            {comment.object_name}
                                                        </Text>
                                                    </div>
                                                )}
                                            </div>
                                            <Text size='s' className={classes.commentBody}>
                                                {comment.body}
                                            </Text>

                                            {comment.child.parent !== null && (
                                                <div className={classes.commentReply}>
                                                    <div className={classes.replyHeader}>
                                                        <Text size='xs' weight='semibold' view='secondary'>
                                                            Ответ: {getUserDisplayName(comment.child)}
                                                        </Text>
                                                        <Text size='xs' view='secondary'>
                                                            {formatDate(comment.child.created_at)}
                                                        </Text>
                                                    </div>
                                                    <Text size='xs' view='secondary' className={classes.replyBody}>
                                                        {comment.child.body}
                                                    </Text>
                                                </div>
                                            )}
                                        </Card>
                                    ))}
                                </div>
                            ) : (
                                <div className={classes.emptyState}>
                                    {/* <IconMessage size="l" view='secondary' /> */}
                                    <Text size='s' view='secondary'>Комментарии отсутствуют</Text>
                                    <Text size='xs' view='secondary' style={{ textAlign: 'center' }}>
                                        Здесь будут отображаться ваши последние комментарии к проектам
                                    </Text>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Модальное окно смены пароля */}
            <Modal
                isOpen={isChangePasswordModalOpen}
                hasOverlay
                onClickOutside={handleClosePasswordModal}
                onEsc={handleClosePasswordModal}
            >
                <div style={{ padding: '24px', width: '400px' }}>
                    <Text size="xl" weight="bold" style={{ marginBottom: '20px' }}>
                        Смена пароля
                    </Text>

                    {/* Поле старого пароля - всегда видно */}
                    <TextField
                        size="s"
                        type="password"
                        label="Старый пароль"
                        placeholder="Введите старый пароль"
                        value={passwordFormData.old_password}
                        onChange={handlePasswordInputChange('old_password')}
                        status={passwordErrors.old_password ? 'alert' : undefined}
                        caption={passwordErrors.old_password}
                        style={{ marginBottom: '16px' }}
                        disabled={isCodeSent || isPasswordBlocked}
                    />

                    {/* Поля, которые появляются после отправки кода */}
                    {isCodeSent && (
                        <>
                            <TextField
                                size="s"
                                label="Код подтверждения"
                                placeholder="Введите код из письма"
                                value={code}
                                onChange={(value) => setCode(value || '')}
                                style={{ marginBottom: '16px' }}
                                disabled={isPasswordBlocked}
                            />

                            <TextField
                                size="s"
                                type="password"
                                label="Новый пароль"
                                placeholder="Введите новый пароль"
                                value={passwordFormData.new_password}
                                onChange={handlePasswordInputChange('new_password')}
                                status={passwordErrors.new_password ? 'alert' : undefined}
                                caption={passwordErrors.new_password}
                                style={{ marginBottom: '16px' }}
                                disabled={isPasswordBlocked}
                            />

                            <TextField
                                size="s"
                                type="password"
                                label="Подтверждение пароля"
                                placeholder="Повторите новый пароль"
                                value={passwordFormData.confirm_password}
                                onChange={handlePasswordInputChange('confirm_password')}
                                status={passwordErrors.confirm_password ? 'alert' : undefined}
                                caption={passwordErrors.confirm_password}
                                style={{ marginBottom: '20px' }}
                                disabled={isPasswordBlocked}
                            />
                        </>
                    )}

                    {/* Отображение ошибки API */}
                    {changePasswordError && (
                        <Text size='s' view='alert' style={{ marginBottom: '16px' }}>
                            {changePasswordError}
                        </Text>
                    )}

                    <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                        <Button
                            size="s"
                            label="Отмена"
                            view="ghost"
                            onClick={handleClosePasswordModal}
                            disabled={isCodeSending || isChangePasswordLoading}
                        />

                        {!isCodeSent ? (
                            <Button
                                size="s"
                                label={isCodeSending ? 'Отправка...' : 'Отправить код'}
                                onClick={handleSendCode}
                                loading={isCodeSending}
                                disabled={isCodeSending || !passwordFormData.old_password.trim() || isPasswordBlocked}
                            />
                        ) : (
                            <Button
                                size="s"
                                label={isChangePasswordLoading ? 'Смена...' : 'Сменить пароль'}
                                onClick={handleChangePassword}
                                loading={isChangePasswordLoading}
                                disabled={isChangePasswordLoading || !code.trim() || isPasswordBlocked || remainingPasswordAttempts === 0}
                            />
                        )}
                    </div>
                </div>
            </Modal>

            {/* Модальное окно загрузки аватарки */}
            <Modal
                isOpen={isAvatarModalOpen}
                hasOverlay
                onClickOutside={handleCloseAvatarModal}
                onEsc={handleCloseAvatarModal}
                style={{ width: '500px' }}
            >
                <div style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '20px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Text size='xl' weight='bold'>Загрузка аватарки</Text>
                        <Button 
                            size='s' 
                            iconLeft={IconClose} 
                            view='clear' 
                            onClick={handleCloseAvatarModal} 
                        />
                    </div>

                    {/* Drag & Drop область */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                        <Text size='m' weight='semibold'>Загрузите изображение аватарки *</Text>
                        
                        <div
                            style={{
                                border: `2px dashed ${isDragOver ? 'var(--color-control-bg-primary)' : 'var(--color-control-bg-border-default)'}`,
                                borderRadius: '8px',
                                padding: '40px 20px',
                                textAlign: 'center',
                                backgroundColor: isDragOver ? 'var(--color-control-bg-primary-hover)' : 'var(--color-bg-secondary)',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                                minHeight: '200px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}
                            onDragOver={handleDragOver}
                            onDragLeave={handleDragLeave}
                            onDrop={handleDrop}
                            onClick={() => document.getElementById('avatar-file-input')?.click()}
                        >
                            <input
                                id="avatar-file-input"
                                type="file"
                                accept="image/*"
                                style={{ display: 'none' }}
                                onChange={handleFileInputChange}
                            />
                            
                            {previewUrl ? (
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' }}>
                                    <div style={{ 
                                        marginTop: '12px', 
                                        maxWidth: '200px', 
                                        maxHeight: '150px', 
                                        overflow: 'hidden', 
                                        borderRadius: '4px',
                                        border: '1px solid var(--color-control-bg-border-default)'
                                    }}>
                                        <img 
                                            src={previewUrl} 
                                            alt="Preview" 
                                            style={{ 
                                                width: '100%', 
                                                height: 'auto', 
                                                objectFit: 'cover',
                                                display: 'block'
                                            }}
                                        />
                                    </div>
                                    <Button
                                        size="xs"
                                        view="ghost"
                                        label="Удалить файл"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setSelectedFile(null);
                                            setPreviewUrl(null);
                                        }}
                                        disabled={isAvatarLoading}
                                    />
                                </div>
                            ) : (
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' }}>
                                    {/*@ts-ignore */}
                                    <IconUpload size="m" view="secondary" />
                                    <div>
                                        <Text size="s" view="primary" weight="semibold">
                                            Перетащите изображение сюда или нажмите для выбора
                                        </Text>
                                        <Text size="xs" view="secondary" style={{ marginTop: '4px' }}>
                                            Поддерживаемые форматы: JPG, PNG, GIF, WEBP
                                        </Text>
                                        <Text size="xs" view="secondary" style={{ marginTop: '2px' }}>
                                            Максимальный размер: 5MB
                                        </Text>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Информация о выбранном файле */}
                    {selectedFile && (
                        <div style={{ 
                            marginTop: '12px', 
                            padding: '12px 16px', 
                            backgroundColor: 'var(--color-bg-secondary)', 
                            borderRadius: '6px',
                            border: '1px solid var(--color-control-bg-border-default)'
                        }}>
                            <Text size="s" style={{ margin: 0, fontWeight: 500 }}>
                                Выбран файл: {selectedFile.name}
                            </Text>
                            <Text size="xs" view="secondary" style={{ margin: '4px 0 0 0' }}>
                                Размер: {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                            </Text>
                        </div>
                    )}

                    {/* Кнопки действий */}
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
                        <Button
                            size="s"
                            view="secondary"
                            label="Отмена"
                            onClick={handleCloseAvatarModal}
                            disabled={isAvatarLoading}
                        />
                        <Button
                            size="s"
                            view="primary"
                            label={isAvatarLoading ? "Загрузка..." : "Загрузить аватар"}
                            onClick={handleAvatarUpload}
                            disabled={!selectedFile || isAvatarLoading}
                            loading={isAvatarLoading}
                        />
                    </div>
                </div>
            </Modal>

            {privacyPolicy && <PrivacyPolicy isOpen={privacyPolicy} setIsOpen={setPrivacyPolicy}
                handleCloseModal={() => setPrivacyPolicy(false)}
            />}
        </div>
    )
}

export default AccountContent